import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Eye, RotateCcw, Loader2 } from "lucide-react";

export const Plantillas = () => {
  const [plantillas, setPlantillas] = useState([]);
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const load = () => api.get("/plantillas").then(({ data }) => setPlantillas(data));
  useEffect(() => { load(); }, []);

  const guardar = async () => {
    setGuardando(true);
    try {
      const { tipo, asunto, titulo, intro, cierre, cta } = edit;
      await api.put(`/plantillas/${tipo}`, { asunto, titulo, intro, cierre, cta });
      toast.success("Plantilla guardada");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const ver = async (p, borrador) => {
    try {
      const body = borrador
        ? { asunto: p.asunto, titulo: p.titulo, intro: p.intro, cierre: p.cierre || "", cta: p.cta }
        : null;
      const { data } = await api.post(`/plantillas/${p.tipo}/preview`, body);
      setPreview({ ...data, nombre: p.nombre });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const restaurar = async (tipo) => {
    await api.post(`/plantillas/${tipo}/reset`);
    toast.success("Plantilla restaurada al texto original");
    setEdit(null);
    load();
  };

  return (
    <div data-testid="plantillas-seccion">
      <h2 className="font-display text-2xl font-extrabold mt-12 mb-2">Plantillas de los correos</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Cambia el texto de cada aviso automático. Las variables entre llaves se sustituyen por los datos reales.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {plantillas.map((p) => (
          <Card key={p.tipo} className="rounded-2xl p-5 border-border shadow-sm" data-testid={`plantilla-${p.tipo}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display font-bold">{p.nombre}</h3>
                <p className="text-xs text-muted-foreground mt-1">{p.asunto}</p>
              </div>
              {p.personalizada && <Badge variant="secondary" className="rounded-full">Editada</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-4 line-clamp-2">{p.intro}</p>
            <div className="flex gap-2 mt-5">
              <Button variant="outline" size="sm" className="rounded-lg"
                onClick={() => setEdit({ ...p, cierre: p.cierre || "" })} data-testid={`editar-plantilla-${p.tipo}`}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg"
                onClick={() => ver(p, false)} data-testid={`ver-plantilla-${p.tipo}`}>
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Previsualizar
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="plantilla-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{edit?.nombre}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-4">
              <p className="text-xs text-muted-foreground">
                Variables disponibles: {edit.variables.map((v) => `{${v}}`).join(", ")}
              </p>
              <div className="grid gap-2">
                <Label>Asunto</Label>
                <Input value={edit.asunto} onChange={(e) => setEdit({ ...edit, asunto: e.target.value })}
                  data-testid="plantilla-asunto-input" />
              </div>
              <div className="grid gap-2">
                <Label>Título dentro del email</Label>
                <Input value={edit.titulo} onChange={(e) => setEdit({ ...edit, titulo: e.target.value })}
                  data-testid="plantilla-titulo-input" />
              </div>
              <div className="grid gap-2">
                <Label>Texto de entrada</Label>
                <Textarea rows={3} value={edit.intro} onChange={(e) => setEdit({ ...edit, intro: e.target.value })}
                  data-testid="plantilla-intro-input" />
              </div>
              <div className="grid gap-2">
                <Label>Texto de cierre</Label>
                <Textarea rows={2} value={edit.cierre} onChange={(e) => setEdit({ ...edit, cierre: e.target.value })}
                  data-testid="plantilla-cierre-input" />
              </div>
              <div className="grid gap-2">
                <Label>Texto del botón</Label>
                <Input value={edit.cta} onChange={(e) => setEdit({ ...edit, cta: e.target.value })}
                  data-testid="plantilla-cta-input" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" className="rounded-full" onClick={() => restaurar(edit.tipo)}
              data-testid="restaurar-plantilla-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> Restaurar original
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => ver(edit, true)}
              data-testid="previsualizar-borrador-btn">
              <Eye className="h-4 w-4 mr-2" /> Previsualizar
            </Button>
            <Button className="rounded-full font-semibold" onClick={guardar} disabled={guardando}
              data-testid="guardar-plantilla-btn">
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="rounded-2xl max-w-2xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{preview?.asunto}</DialogTitle>
          </DialogHeader>
          <iframe title="Vista previa del email" srcDoc={preview?.html || ""}
            className="w-full h-[60vh] rounded-lg border border-border bg-white"
            data-testid="preview-iframe" />
        </DialogContent>
      </Dialog>
    </div>
  );
};
